/**
 * Action creators for the voicemail feature.
 */
import { VOICEMAIL_INBOX_LOADED, VOICEMAIL_INBOX_LOADING, VOICEMAIL_MESSAGE_SELECTED, VOICEMAIL_MESSAGE_DELETED, VOICEMAIL_MESSAGE_SEEN, VOICEMAIL_UNREAD_COUNT_UPDATED, VOICEMAIL_CAPTURE_FAILED, VOICEMAIL_CAPTURE_REQUESTED, VOICEMAIL_DEVICE_TAKE_OPENED, VOICEMAIL_DEVICE_TAKE_START_FAILED, VOICEMAIL_RECORDING_STARTED, VOICEMAIL_RECORDING_COMPLETE, VOICEMAIL_RECORDING_CANCELLED, VOICEMAIL_RECORDING_SENT, VOICEMAIL_PROMPT_SHOWN, VOICEMAIL_PROMPT_DISMISSED, VOICEMAIL_PLAYER_OPENED, VOICEMAIL_PLAYER_CLOSED, VOICEMAIL_ERROR, VOICEMAIL_CLEAR_ERROR } from './actionTypes';
import { fetchUnreadCount, fetchVoicemailInbox, fetchVoicemailMessage, deleteVoicemailMessage as apiDeleteMessage } from './functions';
function getErrorMessage(error) {
    return error instanceof Error ? error.message : 'Voicemail request failed';
}
/**
 * Load the voicemail inbox from the server.
 */
export function loadInbox(limit = 20, offset = 0) {
    return async (dispatch) => {
        dispatch({ type: VOICEMAIL_INBOX_LOADING });
        try {
            const result = await fetchVoicemailInbox(limit, offset);
            dispatch({
                type: VOICEMAIL_INBOX_LOADED,
                messages: result.messages,
                totalCount: result.total,
                unreadCount: result.unreadCount
            });
        }
        catch (error) {
            dispatch({ type: VOICEMAIL_ERROR, error: getErrorMessage(error) });
        }
    };
}
/**
 * Load just the unread count for the floating badge.
 */
export function loadUnreadCount() {
    return async (dispatch) => {
        try {
            const count = await fetchUnreadCount();
            dispatch({
                type: VOICEMAIL_UNREAD_COUNT_UPDATED,
                count
            });
        }
        catch (error) {
            dispatch({ type: VOICEMAIL_ERROR, error: getErrorMessage(error) });
        }
    };
}
/**
 * Select and open a message for playback.
 */
export function openMessage(messageId) {
    return async (dispatch) => {
        try {
            const message = await fetchVoicemailMessage(messageId);
            dispatch({
                type: VOICEMAIL_MESSAGE_SELECTED,
                message
            });
            dispatch({ type: VOICEMAIL_PLAYER_OPENED });
        }
        catch (error) {
            dispatch({ type: VOICEMAIL_ERROR, error: getErrorMessage(error) });
        }
    };
}
/**
 * Close the video player.
 */
export function closePlayer() {
    return { type: VOICEMAIL_PLAYER_CLOSED };
}
/**
 * Delete a voicemail message.
 */
export function removeMessage(messageId) {
    return async (dispatch) => {
        try {
            await apiDeleteMessage(messageId);
            dispatch({ type: VOICEMAIL_MESSAGE_DELETED, messageId });
        }
        catch (error) {
            dispatch({ type: VOICEMAIL_ERROR, error: getErrorMessage(error) });
        }
    };
}
/**
 * Mark a message as seen.
 */
export function markSeen(messageId) {
    return { type: VOICEMAIL_MESSAGE_SEEN, messageId };
}
/**
 * Update the unread count (from WebSocket push).
 */
export function updateUnreadCount(count) {
    return { type: VOICEMAIL_UNREAD_COUNT_UPDATED, count };
}
/**
 * The user pressed the record control. The recorder is only asked to start
 * once this has been dispatched, so a take begins when the person is ready
 * rather than the instant their camera warms up.
 */
export function captureRequested() {
    return { type: VOICEMAIL_CAPTURE_REQUESTED };
}
/**
 * The recorder did not start this take: it refused, never answered, or ended
 * before it captured anything. Nothing was recorded, and record can be
 * pressed again.
 */
export function captureFailed(messageId) {
    return { type: VOICEMAIL_CAPTURE_FAILED, messageId };
}
/**
 * Recording started (from WebSocket or REST response).
 */
export function recordingStarted(session) {
    return { type: VOICEMAIL_RECORDING_STARTED, session };
}
/**
 * The camera screen opened for a device take that the server has not started
 * yet (session.pending, no message id).
 */
export function deviceTakeOpened(session) {
    return { type: VOICEMAIL_DEVICE_TAKE_OPENED, session };
}
/**
 * The server did not start the take of this opening.
 */
export function deviceTakeStartFailed(attemptId, error) {
    return { type: VOICEMAIL_DEVICE_TAKE_START_FAILED, attemptId, error };
}
/**
 * Recording completed (from WebSocket).
 */
export function recordingComplete(messageId, durationSeconds, kind = 'message') {
    return { type: VOICEMAIL_RECORDING_COMPLETE, messageId, durationSeconds, kind };
}
/**
 * Recording cancelled.
 */
export function recordingCancelled() {
    return { type: VOICEMAIL_RECORDING_CANCELLED };
}
/**
 * Recording sent to the recipient inbox.
 */
export function recordingSent(messageId) {
    return { type: VOICEMAIL_RECORDING_SENT, messageId };
}
/**
 * Show the "leave video message?" prompt after a missed call.
 */
export function showPrompt(data) {
    return { type: VOICEMAIL_PROMPT_SHOWN, data };
}
/**
 * Dismiss the voicemail prompt.
 */
export function dismissPrompt() {
    return { type: VOICEMAIL_PROMPT_DISMISSED };
}
/**
 * Clear the current error.
 */
export function clearError() {
    return { type: VOICEMAIL_CLEAR_ERROR };
}
